import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import {
    Text,
    Theme,
    Button,
    Loader,
    Modal,
    TextInput,
    BubbleSelectBlock,
    DateInput
} from '@simplybenefits/sb-ui-library';
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell/TableCell";
import TableBody from "@mui/material/TableBody";
import * as dates from "@simplybenefits/sb-dates";
import { dispatchGetAccountNotes, createAccountNote, removeAccountNote } from '../../../actions/accounts.js';
import FormControl from "@mui/material/FormControl";
import { helpers } from "@simplybenefits/sb-utils";

class AccountNotes extends PureComponent {
    state = {
        notes: null,
        selectedNote: null,
        selectedClass: null,
        selectedDivision: null,
        scope: null,
        createNoteModalOpen: false,
        body: '',
        expiry: null,
        showExpiry: null
    };

    async componentDidMount() {
        await this._getNotes(this.props.currentAccountMeta.accountID);
    }

    _getNotes = async (accountID, classID = null, divisionID = null) => {
        const notes = await this.props.dispatch(dispatchGetAccountNotes(accountID, classID, divisionID));

        if (notes) {
            this.setState({
                notes,
                scope: null,
                createNoteModalOpen: false,
                body: '',
                expiry: null,
                selectedNote: null,
                selectedClass: null,
                selectedDivision: null,
                selectedClassError: null,
                selectedDivisionError: null,
                showExpiry: null
            });
        }
    };

    _createNote = async () => {
        const expiry = this.state.expiry ? dates.getUTC(this.state.expiry) : null;
        const accountID = this.props.currentAccountMeta.accountID;
        let classID = null;
        let divisionID = null;

        if (this.state.scope === 'Class') {
            if (!this.state.selectedClass) {
                this.setState({
                    selectedClassError: 'You must select a Class to apply the note to.'
                });

                return;
            }

            classID = Object.keys(this.props.classData).find((key) => this.props.classData[key].title === this.state.selectedClass);
        }

        if (this.state.scope === 'Division') {
            if (!this.state.selectedDivision) {
                this.setState({
                    selectedDivisionError: 'You must select a Division to apply the note to.'
                });

                return;
            }

            divisionID = Object.keys(this.props.divisions).find((key) => this.props.divisions[key].title === this.state.selectedDivision);
        }

        const renewalID = null;
        const note = await this.props.dispatch(createAccountNote(accountID, classID, divisionID, renewalID, this.state.body, expiry));

        if (!note) {
            return;
        }

        this.setState({
            notes: [ note, ...this.state.notes ],
            scope: null,
            createNoteModalOpen: false,
            body: '',
            expiry: null,
            selectedNote: null,
            selectedClass: null,
            selectedDivision: null,
            selectedClassError: null,
            selectedDivisionError: null,
            showExpiry: null
        });
    };

    /**
     * @param note
     * @returns {Promise<null>}
     * @private
     */
    _removeNote = async (note) => {
        const accountID = this.props.currentAccountMeta.accountID;

        const success = await this.props.dispatch(removeAccountNote(accountID, note.accNoteID));

        if (!success) {
            return;
        }

        this.setState({
            notes: this.state.notes.filter((item) => note.accNoteID !== item.accNoteID)
        });
    };

    _toggleNote = (note) => {
        if (note !== this.state.selectedNote) {
            this.setState({
                selectedNote: note
            });

            return;
        }

        if (this.state.selectedNote) {
            this.setState({
                selectedNote: null
            });
        } else {
            this.setState({
                selectedNote: note
            });
        }
    };

    /**
     * @param body
     * @returns {string}
     * @private
     */
    _truncate = (body) => {
        if (body.length > 80) {
            return `${ body.substring(0, 80) }...`;
        }

        return body;
    };

    _handleChange = (field, value) => {
        this.setState({
            [field]: value
        });
    };

    render() {
        if (!this.state.notes) {
            return <Loader />;
        }

        const { notes, selectedNote } = this.state;

        const styles = {
            container: {
                flex: 1,
                border: "2px solid #F1F1F1",
                borderRadius: 5,
                backgroundColor: '#FFF',
                padding: 20,
                marginTop: 10
            },
            table: {
                display: 'block',
                overflowX: 'auto',
                paddingBottom: '20px',
                width: '100%',
                border: "2px solid #F1F1F1",
                backgroundColor: '#FFF'
            },
            scrollbar: {
                '&::webkitScrollbar': {
                    height: '15px',
                    width: '15px'
                },
                '&::webkitSrollbarThumb': {
                    backgroundColor: Theme.colors.surface,
                    borderRadius: '10px',
                    boxShadow: 'inset 0 0 2px rgba(0, 0, 0, .5)'
                },
                '&::webkitScrollbarTrack': {
                    backgroundColor: Theme.colors.backgroundGrey,
                    borderRadius: '10px',
                    boxShadow: 'inset 0 0 2px rgba(0, 0, 0, 0.2)'
                }
            },
            tableHeading: {
                paddingTop: "10px",
                paddingBottom: "10px",
                paddingRight: "2vw",
                paddingLeft: "2vw",
                fontFamily: Theme.fonts.type.heading,
                color: Theme.colors.surface,
                whiteSpace: "nowrap",
                border: "none",
                flex: 1
            },
            tableCell: {
                paddingTop: "10px",
                paddingBottom: "10px",
                fontFamily: Theme.fonts.type.light,
                whiteSpace: "nowrap",
                paddingRight: "2vw",
                paddingLeft: "2vw",
                color: Theme.colors.mainTextGrey,
                flex: 1
            },
            titleBlock: {
                display: 'flex',
                flex: 1,
                flexDirection: 'row',
                marginTop: 30
            }
        };

        const classData = [];

        helpers.sortObjByTitle(this.props.classData).map((classID) => {
            const classMeta = this.props.classData[classID];

            // don't show parent class for mod flex
            if (classMeta.isModular && !classMeta.parentClassID) {
                return null;
            }

            classData.push({
                key: classID,
                value: this.props.classData[classID].title
            });

            return null;
        });

        const divisions = helpers.sortObjByTitle(this.props.divisions).map((divisionID) => ({
            key: divisionID,
            value: this.props.divisions[divisionID].title
        }));

        return (
            <div>
                <div style={ styles.titleBlock }>
                    <Text
                        largest
                        veryBold
                        color={ Theme.colors.mainTextGrey }
                        customStyle={{ flex: 1 }}
                    >
                        Account Notes
                    </Text>

                    <Button
                        color="primary"
                        customStyle={{
                            height: 50,
                            width: 220,
                            marginLeft: "10px",
                            textTransform: "capitalize",
                            fontSize: 18
                        }}
                        onClick={() => {
                            this.setState({
                                createNoteModalOpen: true,
                                scope: null,
                                body: '',
                                expiry: null,
                                selectedNote: null,
                                selectedClass: null,
                                selectedDivision: null,
                                selectedClassError: null,
                                selectedDivisionError: null,
                                showExpiry: null
                            });
                        }}
                    >
                        Create Note
                    </Button>
                </div>

                <div style={ styles.container }>
                    <div style={{ overflow: "auto", maxHeight: 600, ...styles.scrollbar }}>
                        <Table>
                            <TableHead>
                                <TableRow sx={{ backgroundColor: Theme.colors.skyBlue }}>
                                    <TableCell style={styles.tableHeading}>
                                        Note
                                    </TableCell>

                                    <TableCell style={styles.tableHeading}>
                                        Class
                                    </TableCell>

                                    <TableCell style={styles.tableHeading}>
                                        Division
                                    </TableCell>

                                    <TableCell style={styles.tableHeading}>
                                        Author
                                    </TableCell>

                                    <TableCell style={styles.tableHeading}>
                                        Created
                                    </TableCell>

                                    <TableCell style={styles.tableHeading}>
                                        Expires
                                    </TableCell>

                                    <TableCell style={styles.tableHeading} />
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {
                                    (!notes || notes.length === 0) &&
                                        <Text
                                            medium
                                            color={ Theme.colors.mainTextGrey }
                                            margin="10px 0 0 5px"
                                        >
                                            No notes available.
                                        </Text>
                                }

                                {
                                    notes.map((note, index) => {
                                        let backgroundObject = { border: "none" };
                                        if (index % 2 !== 0) {
                                            backgroundObject = { backgroundColor: Theme.colors.backgroundGrey, border: "none" };
                                        }

                                        return (
                                            <React.Fragment key={note.accNoteID}>
                                                <TableRow onClick={ () => this._toggleNote(note) } style={{ cursor: 'pointer' }}>
                                                    <TableCell style={{ ...styles.tableCell, ...backgroundObject }}>
                                                        { this._truncate(note.body) }
                                                    </TableCell>

                                                    <TableCell style={{ ...styles.tableCell, ...backgroundObject }}>
                                                        {
                                                            (
                                                                note.classID === null && note.divisionID === null ?
                                                                    'All Classes'
                                                                    :
                                                                    note.classID === null && note.divisionID ?
                                                                        '-'
                                                                        :
                                                                        this.props.classData[note.classID].title
                                                            )
                                                        }
                                                    </TableCell>

                                                    <TableCell style={{ ...styles.tableCell, ...backgroundObject }}>
                                                        {
                                                            (
                                                                note.divisionID === null && note.classID === null ?
                                                                    'All Divisions'
                                                                    :
                                                                    note.divisionID === null && note.classID ?
                                                                        '-'
                                                                        :
                                                                        this.props.divisions[note.divisionID].title
                                                            )
                                                        }
                                                    </TableCell>

                                                    <TableCell style={{ ...styles.tableCell, ...backgroundObject }}>
                                                        { note.createdBy }
                                                    </TableCell>

                                                    <TableCell style={{ ...styles.tableCell, ...backgroundObject }} >
                                                        { dates.getLocalDisplay(note.dateAdded, 'lll') }
                                                    </TableCell>

                                                    <TableCell style={{ ...styles.tableCell, ...backgroundObject }} >
                                                        {
                                                            note.expiry ?
                                                                note.status.toUpperCase() === 'EXPIRED' ?
                                                                    `Expired ${ dates.getLocalDisplay(note.expiry, 'll') }`
                                                                    :
                                                                    dates.getLocalDisplay(note.expiry, 'll')
                                                                :
                                                                'Never'
                                                        }
                                                    </TableCell>

                                                    <TableCell style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-end', ...styles.tableCell, ...backgroundObject }}>
                                                        <Button transparent onClick={ () => this._removeNote(note) }>
                                                            Remove
                                                        </Button>
                                                    </TableCell>
                                                </TableRow>

                                                {
                                                    selectedNote?.accNoteID === note.accNoteID &&
                                                        <TableRow>
                                                            <TableCell style={{ ...styles.tableCell, ...backgroundObject }} colSpan={12}>
                                                                <Text small formatted bold customStyle={{ ...backgroundObject }}>{ note.body }</Text>
                                                            </TableCell>
                                                        </TableRow>
                                                }
                                            </React.Fragment>
                                        );
                                    })
                                }
                            </TableBody>
                        </Table>
                    </div>
                </div>

                <Modal
                    open={ this.state.createNoteModalOpen }
                    onClose={ () => this.setState({ createNoteModalOpen: false })}
                    title="Create Account Note"
                    primaryButton={{
                        label: "Create",
                        onClick: this._createNote
                    }}
                    loader={ this.props.loader }
                    large
                >
                    <FormControl component="fieldset">
                        <Text small>
                            Select if this note applies to the entire account or only to a specific class or division.
                        </Text>

                        <BubbleSelectBlock
                            first={{
                                title: 'Account',
                                selected: this.state.scope === 'Account',
                                field: 'Account'
                            }}
                            second={{
                                title: 'Class',
                                selected: this.state.scope === 'Class',
                                field: 'Class'
                            }}
                            third={{
                                title: 'Division',
                                selected: this.state.scope === 'Division',
                                field: 'Division'
                            }}
                            handleChange={(field) => {
                                this.setState({ scope: field });
                            }}
                        />
                    </FormControl>

                    {
                        this.state.scope === 'Class' &&
                            <TextInput
                                variant="outlined"
                                label="Class"
                                value={ this.state.selectedClass || '' }
                                onChange={(e) => this._handleChange('selectedClass', e.target.value)}
                                fullWidth
                                required
                                select
                                selectOptions={ classData }
                                error={Boolean(this.state.selectedClassError)}
                                helperText={this.state.selectedClassError}
                            />
                    }

                    {
                        this.state.scope === 'Division' &&
                            <TextInput
                                variant="outlined"
                                label="Division"
                                value={ this.state.selectedDivision || '' }
                                onChange={(e) => this._handleChange('selectedDivision', e.target.value)}
                                fullWidth
                                required
                                select
                                selectOptions={ divisions }
                                error={Boolean(this.state.selectedDivisionError)}
                                helperText={this.state.selectedDivisionError}
                            />
                    }

                    <FormControl component="fieldset">
                        <Text small>
                            Does this note have an expiry date?
                        </Text>

                        <BubbleSelectBlock
                            first={{
                                title: 'Yes',
                                selected: this.state.showExpiry === true,
                                field: 'Yes'
                            }}
                            second={{
                                title: 'No',
                                selected: this.state.showExpiry === false,
                                field: 'No'
                            }}
                            handleChange={(field) => {
                                this.setState({ showExpiry: field === 'Yes' });
                            }}
                        />
                    </FormControl>

                    {
                        this.state.showExpiry &&
                            <DateInput
                                label="Expiry Date (YYYY/MM/DD)"
                                value={this.state.expiry}
                                variant="outlined"
                                onChange={(e) => this._handleChange('expiry', e.target.value)}
                                fullWidth
                                required
                            />
                    }

                    <TextInput
                        fullWidth
                        value={ this.state.body || '' }
                        variant="outlined"
                        onChange={(e) => this._handleChange("body", e.target.value)}
                        multiline={ true }
                        rows={ 5 }
                        backgroundColor="#FFFFFF"
                        placeholder="Add your note here..."
                    />
                </Modal>
            </div>
        );
    }
}

export default (connect()(AccountNotes));